/**
 * harness/DismissibleToast.jsx
 * T-Cron-2026-10-31 08:00 早间 — 改进清单 #10「无障碍」+ 一致性缺口收口
 *
 * 背景(取证):
 *   `grep -rn 'download-toast' vite/src/components/*.jsx` → **4 处**:
 *     · components/PostDetail.jsx:1094      (下载结果)
 *     · components/BatchActionBar.jsx:174   (批量操作结果)
 *     · components/FavoriteBar.jsx:138      (收藏栏操作结果)
 *     · App.jsx:788                         (导出结果)
 *   4 处的 JSX 结构**完全一致**,都只有 `role="status" aria-live="polite"`。
 *
 *   问题(为什么这是个真缺口而不是洁癖):
 *   ① `.download-toast` 的 CSS 里写着 `pointer-events: none`
 *      ⇒ toast **在物理上就不可能被点击**,哪怕给它加 onClick 也没用。
 *      老仓库(T62)给 `.toast` 加过「点击关闭」,Vite 迁移时
 *      这条能力连同 pointer-events 的覆盖一起丢了 ⇒ 能力回退。
 *   ② 4 处都没有 Escape 关闭、没有 tabIndex ⇒ 键盘用户没法主动关掉它。
 *      toast 挡住卡片时(它 `position:fixed; bottom:96px`,正好压住
 *      移动端底部导航上方的卡片),用户只能干等 2.6s 自动消失。
 *   ③ 4 处各自复制 `setTimeout(..., 2600)` + `clearTimeout` 的样板,
 *      逻辑重复 ⇒ 改一处漏三处的风险。
 *
 * 为什么做成共享组件而不是改 4 遍:
 *   收口到一处 = 以后再加工具提示(比如加「撤销」按钮)只改一个文件,
 *   也保证 4 个入口的键盘/读屏行为**逐字一致**。
 *
 * 契约:
 *   · Escape 关闭(只在 toast 自身获得焦点时响应,见下);
 *   · 点击关闭 —— 需要 CSS 把 pointer-events 从 none 改成 auto;
 *   · 键盘可达:tabIndex={0} ⇒ Tab 能停在 toast 上,按 Enter/Space 也能关;
 *   · 不抢焦点:toast 出现时**不**自动 focus(否则会打断用户正在打的字);
 *     Escape 的监听挂在 toast 元素自身的 onKeyDown 上,
 *     语义是「用户已经 Tab 到这个 toast 上,按 Esc 关掉它」。
 *   · role="status" + aria-live="polite" 保持不变 ⇒ 读屏播报行为不回退。
 *
 * pointer-events 的处理:
 *   改成 `auto` 之后,toast 会挡住它底下的内容 —— 但它本来就是 fixed 浮层,
 *   视觉上就在最上层,且只在 2.6s 内存在。真正的风险是「用户想点 toast 背后的
 *   卡片,结果点到 toast 上」⇒ 所以加 title="点击关闭",并且 toast 的
 *   背景是半透明深色(视觉上明确是个浮层),用户一眼能看出它挡路。
 *
 * 样式契约(见 a11y.css 的 `.download-toast` 补充块):
 *   .download-toast { pointer-events: auto; cursor: pointer; }
 *
 * ⚠️ 不要显式 `import React from 'react'` —— vite.config.js 的
 * esbuild.jsxInject 已经给每个 .jsx 注入 React,重复 import 会报
 * "Identifier \"React\" has already been declared" 并让整轮构建失败。
 */

/**
 * @param {object} props
 * @param {string} props.message          要显示的文案(空字符串 = 不渲染)
 * @param {() => void} props.onDismiss     关闭回调
 * @param {string} [props.className]       追加的 class
 * @param {string} [props.label]           @deprecated 保留兼容,**不生效**。
 *        原因:给 live region 命名会让 NVDA/JAWS 播报可访问名而不是
 *        文本内容,反而丢掉「已复制 3 篇」这类真正的信息。
 *        要改播报文案请直接改 message。
 */
export function DismissibleToast({ message, onDismiss, className }) {
  if (!message) return null;

  const handleKeyDown = (e) => {
    // 🔴 T137 (2026-11-03 08:00 早间) — 长按(auto-repeat)守卫。
    // 按住 Enter 不放 ⇒ 反复 onDismiss(本无害,但 onDismiss 可能带副作用)
    //   ⇒ 统一在入口挡掉单键 repeat。
    if (e.repeat && !(e.ctrlKey || e.metaKey || e.altKey)) return;
    // Enter / Space:语义上等同于「点一下」
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (onDismiss) onDismiss();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onDismiss) onDismiss();
    }
  };

  const cls = className ? `download-toast ${className}` : 'download-toast';

  return (
    <div
      className={cls}
      role="status"
      aria-live="polite"
      // ⚠️ 这里**故意不加 aria-label**。
      // 命名一个 live region(给它 aria-label / aria-labelledby)会让
      // 部分读屏软件(NVDA / JAWS)改为播报「可访问名」而不是「文本内容」
      // —— 那就会把「已复制 3 篇」念成「提示」,反而丢掉了真正要播报的信息。
      //
      // 原实现这里写的是 aria-label={`提示:${message}`},单看没问题
      // (message 也在文本里),但一旦将来有人给 label 传一个静态文案
      // (比如 label="操作结果"),播报就会退化成永远念那一句。
      // ⇒ 文本内容本身就是最好的可访问名,不需要 aria-label。
      aria-keyshortcuts="Escape"
      tabIndex={0}
      title="点击或按 Esc 关闭"
      onClick={() => onDismiss && onDismiss()}
      onKeyDown={handleKeyDown}
    >
      {message}
    </div>
  );
}

export default DismissibleToast;
